<template>
  <v-card outlined :class="hasError ? 'card-error' : ''">
    <v-toolbar flat>
      <v-card-subtitle>{{ label }}</v-card-subtitle>
      <v-spacer />
      <slot name="toolbar" />
      <v-btn
        class="mr-1"
        fab
        outlined
        small
        color="rgb(100, 100, 100, 0.2)"
        :aria-label="$t('conditionBuilder.a11y.addBlock')"
        @click="addConditionBlock()"
      >
        <v-icon color="primary">mdi-plus</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-subtitle v-if="subtitle" class="pt-0">{{ subtitle }}</v-card-subtitle>
    <v-container fluid>
      <div v-if="!blocks.length && $scopedSlots.empty">
        <slot name="empty" />
      </div>
      <template v-for="(block, indexBloc) in blocks">
        <div v-if="indexBloc > 0 && $scopedSlots['block-separator']" :key="`sep-${block._uid}`">
          <slot name="block-separator" :index="indexBloc" />
        </div>
        <v-card :key="block._uid" outlined :class="block.conditions.length ? 'mb-2' : 'mb-2 card-error'">
          <v-toolbar flat>
            <slot name="block-header" :block="block" :index="indexBloc" />
            <v-spacer />
            <v-btn
              class="mr-1"
              color="primary"
              outlined
              :aria-label="$t('conditionBuilder.a11y.addCondition')"
              @click="addCondition(block)"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
            <v-btn
              color="primary"
              outlined
              :aria-label="$t('conditionBuilder.a11y.removeBlock')"
              @click="removeBlock(indexBloc)"
            >
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </v-toolbar>
          <v-container fluid v-if="block.conditions.length">
            <v-row v-for="(condition, index) in block.conditions" :key="condition._uid">
              <v-col cols="12" :sm="valuesToCheckAreArrayOfObject(condition.operator) ? '6' : '4'">
                <validation-provider class="width-100" rules="required" :name="$t('trigger.field')" v-slot="{ errors }">
                  <slot
                    name="field"
                    :condition="condition"
                    :errors="errors"
                    :update="(value) => updateField(condition, value)"
                  >
                    <v-combobox
                      :value="condition.field"
                      dense
                      :items="fields"
                      :label="$t('trigger.field')"
                      hide-details="auto"
                      :error-messages="errors"
                      @input="updateField(condition, $event)"
                    />
                  </slot>
                </validation-provider>
              </v-col>
              <v-col
                :cols="valuesToCheckAreArrayOfObject(condition.operator) ? '10' : '12'"
                :sm="valuesToCheckAreArrayOfObject(condition.operator) ? '5' : '3'"
              >
                <validation-provider
                  class="width-100"
                  rules="required"
                  :name="$t('trigger.operator')"
                  v-slot="{ errors }"
                >
                  <v-autocomplete
                    :value="condition.operator"
                    dense
                    :items="resolvedOperators"
                    :label="$t('trigger.operator')"
                    hide-details="auto"
                    :error-messages="errors"
                    @input="updateOperator(condition, $event)"
                  />
                </validation-provider>
              </v-col>
              <v-col v-if="!valuesToCheckAreArrayOfObject(condition.operator)" cols="10" sm="4">
                <!--
                  Unlike #field, the value column's validation-provider lives INSIDE the
                  default slot content only: overriding #value bypasses it, so consumers
                  MUST supply their own validation-provider (see OrderVisibilitySettings).
                  This is intentional — it lets typed-editor consumers make the "required"
                  rule conditional (e.g. skip it until a field is chosen).
                -->
                <slot
                  name="value"
                  :condition="condition"
                  :is-multi-value="isMultiValueOperator(condition.operator)"
                  :update="(value) => updateValue(condition, value)"
                >
                  <validation-provider
                    class="width-100"
                    rules="required"
                    :name="$t('trigger.value')"
                    v-slot="{ errors }"
                  >
                    <v-combobox
                      v-if="isMultiValueOperator(condition.operator)"
                      :value="condition.values_to_check"
                      dense
                      :label="$t('trigger.value')"
                      hide-details="auto"
                      multiple
                      :error-messages="errors"
                      @input="updateValue(condition, $event)"
                    />
                    <v-text-field
                      v-else
                      :value="condition.values_to_check"
                      dense
                      :label="$t('trigger.value')"
                      hide-details="auto"
                      :error-messages="errors"
                      @input="updateValue(condition, $event)"
                    />
                  </validation-provider>
                </slot>
              </v-col>
              <v-col cols="2" sm="1" class="pt-7 pb-0">
                <v-row justify="center" no-gutters>
                  <v-btn
                    icon
                    :aria-label="$t('conditionBuilder.a11y.removeCondition')"
                    @click="removeCondition(block, index)"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-row>
              </v-col>
              <v-col v-if="valuesToCheckAreArrayOfObject(condition.operator)" class="pt-0" cols="12">
                <v-container fluid>
                  <v-card outlined :class="condition.values_to_check.length ? '' : 'card-error'">
                    <v-container fluid>
                      <v-row
                        v-for="(subCondition, subIndex) in condition.values_to_check"
                        :key="subCondition._uid"
                        class="mb-3"
                      >
                        <v-col cols="12" sm="4">
                          <validation-provider
                            class="width-100"
                            rules="required"
                            :name="$t('trigger.field')"
                            v-slot="{ errors }"
                          >
                            <v-combobox
                              :value="subCondition.field"
                              dense
                              :items="fields"
                              :label="$t('trigger.field')"
                              hide-details="auto"
                              :error-messages="errors"
                              @input="updateField(subCondition, $event)"
                            />
                          </validation-provider>
                        </v-col>
                        <v-col cols="12" sm="3">
                          <validation-provider
                            class="width-100"
                            rules="required"
                            :name="$t('trigger.operator')"
                            v-slot="{ errors }"
                          >
                            <v-autocomplete
                              :value="subCondition.operator"
                              dense
                              :items="scalarOperators"
                              :label="$t('trigger.operator')"
                              hide-details="auto"
                              :error-messages="errors"
                              @input="updateOperator(subCondition, $event)"
                            />
                          </validation-provider>
                        </v-col>
                        <v-col cols="10" sm="4">
                          <validation-provider
                            class="width-100"
                            rules="required"
                            :name="$t('trigger.value')"
                            v-slot="{ errors }"
                          >
                            <v-combobox
                              v-if="isMultiValueOperator(subCondition.operator)"
                              :value="subCondition.values_to_check"
                              dense
                              :label="$t('trigger.value')"
                              hide-details="auto"
                              multiple
                              :error-messages="errors"
                              @input="updateValue(subCondition, $event)"
                            />
                            <v-text-field
                              v-else
                              :value="subCondition.values_to_check"
                              dense
                              :label="$t('trigger.value')"
                              hide-details="auto"
                              :error-messages="errors"
                              @input="updateValue(subCondition, $event)"
                            />
                          </validation-provider>
                        </v-col>
                        <v-col cols="2" sm="1" class="pt-7 pb-0">
                          <v-row justify="center" no-gutters>
                            <v-btn
                              icon
                              :aria-label="$t('conditionBuilder.a11y.removeCondition')"
                              @click="removeSubCondition(condition, subIndex)"
                            >
                              <v-icon>mdi-close</v-icon>
                            </v-btn>
                          </v-row>
                        </v-col>
                      </v-row>
                      <v-row no-gutters justify="center">
                        <v-btn
                          small
                          color="primary"
                          outlined
                          icon
                          :aria-label="$t('conditionBuilder.a11y.addSubCondition')"
                          @click="addSubCondition(condition)"
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </v-row>
                      <v-spacer />
                    </v-container>
                  </v-card>
                </v-container>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </template>
    </v-container>
  </v-card>
</template>

<script>
  import { ValidationProvider } from 'vee-validate'

  const ARRAY_OF_OBJECT_OPERATORS = new Set(['contains_elements_with', 'does_not_contain_elements_with'])
  // Operators whose value is an array. Kept local so this component stays domain-agnostic;
  // it must stay in sync with `MULTI_VALUE_OPERATORS` in `helpers/orderVisibility.js` (the
  // API contract source). Consumers should read arity from the `is-multi-value` slot prop
  // rather than re-deriving it, to avoid the two drifting.
  const MULTI_VALUE_OPERATORS = new Set(['in', 'not_in'])

  export default {
    name: 'ConditionBuilder',
    components: { ValidationProvider },
    props: {
      value: {
        type: Array,
        required: true,
      },
      fields: {
        type: Array,
        required: true,
      },
      hasError: {
        type: Boolean,
        default: false,
      },
      label: {
        type: String,
        default() {
          return this.$t('list.conditionsBlock')
        },
      },
      subtitle: {
        type: String,
        default: '',
      },
      /**
       * Selectable operators as `[{ text, value }]`. When omitted, falls back to the
       * full Trigger/List operator set via the `defaultOperators` computed (kept as a
       * computed rather than a prop-default factory so labels stay reactive to live
       * locale switches for the Trigger/List consumers that rely on the default).
       */
      operators: {
        type: Array,
        default: null,
      },
      /**
       * When true, a condition value is cleared as soon as its field changes.
       * Off by default to preserve the free-text Trigger/List behaviour; the
       * orders-visibility form enables it because the value editor is field-typed.
       */
      resetValueOnFieldChange: {
        type: Boolean,
        default: false,
      },
      /**
       * When true, switching an operator from a multi-value one (`in`/`not_in`) to a
       * scalar one keeps the first selected value instead of comma-joining the array.
       * Enabled by typed-editor consumers (selects) where a joined string is not a
       * valid item; off by default to preserve the free-text Trigger/List behaviour.
       */
      collapseMultiValueToFirst: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        // Internal model mirroring `value` with a stable `_uid` per node so list
        // keys survive add/remove and never mismatch validation state. `_uid` is
        // stripped on emit so the parent/back only ever sees the clean contract.
        blocks: [],
        lastEmitted: null,
        uidCounter: 0,
      }
    },
    computed: {
      // Kept as a computed (not a prop-default factory) so operator labels re-evaluate
      // on live locale switches — a prop default would freeze them at mount time.
      defaultOperators() {
        return [
          { text: this.$t('trigger.equals'), value: 'equals' },
          { text: this.$t('trigger.not_equals'), value: 'not_equals' },
          { text: this.$t('trigger.inferior'), value: 'inferior' },
          { text: this.$t('trigger.inferior_or_equal'), value: 'inferior_or_equal' },
          { text: this.$t('trigger.superior'), value: 'superior' },
          { text: this.$t('trigger.superior_or_equal'), value: 'superior_or_equal' },
          { text: this.$t('trigger.in'), value: 'in' },
          { text: this.$t('trigger.not_in'), value: 'not_in' },
          { text: this.$t('trigger.containsElementWith'), value: 'contains_elements_with' },
          { text: this.$t('trigger.doesNotContainElementsWith'), value: 'does_not_contain_elements_with' },
        ]
      },
      resolvedOperators() {
        return this.operators || this.defaultOperators
      },
      scalarOperators() {
        return this.resolvedOperators.filter((operator) => !this.valuesToCheckAreArrayOfObject(operator.value))
      },
    },
    created() {
      this.syncFromValue(this.value)
    },
    watch: {
      value(newValue) {
        // Skip the echo of our own emit to keep internal `_uid`s stable. This relies on
        // reference identity: parents must forward the emitted array as-is (no cloning on
        // the @input path), otherwise every keystroke resyncs and loses _uid stability.
        if (newValue === this.lastEmitted) {
          return
        }
        this.syncFromValue(newValue)
      },
    },
    methods: {
      nextUid() {
        this.uidCounter += 1
        return this.uidCounter
      },
      valuesToCheckAreArrayOfObject(operator) {
        return ARRAY_OF_OBJECT_OPERATORS.has(operator)
      },
      isMultiValueOperator(operator) {
        return MULTI_VALUE_OPERATORS.has(operator)
      },
      syncFromValue(value) {
        const source = Array.isArray(value) ? value : []
        this.blocks = source.map((block) => ({
          _uid: this.nextUid(),
          conditions: (Array.isArray(block) ? block : []).map((condition) => this.wrapCondition(condition)),
        }))
      },
      // Single source for coercing a stored value into the array a multi-value operator
      // (in/not_in) expects — used on both initial load (wrapCondition) and operator switch
      // (resetConditionValue) so the two paths can't drift. A comma-joined string (legacy or
      // manually-edited data) splits into its elements; a lone scalar becomes one item.
      toMultiValueArray(rawValue) {
        if (Array.isArray(rawValue)) {
          return [...rawValue]
        }
        return rawValue == null ? [] : String(rawValue).split(',')
      },
      wrapCondition(condition) {
        const operator = condition?.operator
        const wrapped = { _uid: this.nextUid(), field: condition?.field, operator }
        const rawValue = condition?.values_to_check
        if (this.valuesToCheckAreArrayOfObject(operator)) {
          wrapped.values_to_check = (Array.isArray(rawValue) ? rawValue : []).map((sub) => this.wrapCondition(sub))
        } else if (MULTI_VALUE_OPERATORS.has(operator)) {
          wrapped.values_to_check = this.toMultiValueArray(rawValue)
        } else {
          wrapped.values_to_check = rawValue
        }
        return wrapped
      },
      unwrapCondition(condition) {
        let valuesToCheck = condition.values_to_check
        if (this.valuesToCheckAreArrayOfObject(condition.operator)) {
          valuesToCheck = (Array.isArray(valuesToCheck) ? valuesToCheck : []).map((sub) => this.unwrapCondition(sub))
        } else if (Array.isArray(valuesToCheck)) {
          valuesToCheck = [...valuesToCheck]
        }
        return { field: condition.field, operator: condition.operator, values_to_check: valuesToCheck }
      },
      emitModel() {
        const output = this.blocks.map((block) => block.conditions.map((condition) => this.unwrapCondition(condition)))
        this.lastEmitted = output
        this.$emit('input', output)
      },
      newCondition() {
        return { _uid: this.nextUid(), field: undefined, operator: undefined, values_to_check: undefined }
      },
      addConditionBlock() {
        this.blocks.push({ _uid: this.nextUid(), conditions: [this.newCondition()] })
        this.emitModel()
      },
      removeBlock(indexBloc) {
        this.blocks.splice(indexBloc, 1)
        this.emitModel()
      },
      addCondition(block) {
        block.conditions.push(this.newCondition())
        this.emitModel()
      },
      removeCondition(block, index) {
        block.conditions.splice(index, 1)
        this.emitModel()
      },
      addSubCondition(condition) {
        if (!Array.isArray(condition.values_to_check)) {
          condition.values_to_check = []
        }
        condition.values_to_check.push(this.newCondition())
        this.emitModel()
      },
      removeSubCondition(condition, subIndex) {
        condition.values_to_check.splice(subIndex, 1)
        this.emitModel()
      },
      updateField(condition, value) {
        condition.field = value
        if (this.resetValueOnFieldChange) {
          // Reset to an operator-appropriate empty value. Array-valued operators
          // (array-of-object and multi-value) must stay an array — the template reads
          // `.length` on them; scalar operators reset to null (never undefined, which
          // would resolve a bound child's default factory).
          const arrayValued =
            this.valuesToCheckAreArrayOfObject(condition.operator) || MULTI_VALUE_OPERATORS.has(condition.operator)
          condition.values_to_check = arrayValued ? [] : null
        }
        this.emitModel()
      },
      updateOperator(condition, value) {
        const previousOperator = condition.operator
        condition.operator = value
        this.resetConditionValue(condition, previousOperator)
        this.emitModel()
      },
      updateValue(condition, value) {
        condition.values_to_check = value
        this.emitModel()
      },
      resetConditionValue(condition, previousOperator) {
        const operator = condition.operator
        if (this.valuesToCheckAreArrayOfObject(operator)) {
          condition.values_to_check = []
          return
        }
        // Leaving an array-of-object operator: `values_to_check` is a stale array of
        // wrapped sub-conditions (each carrying an internal `_uid`) that must never leak
        // or be coerced to garbage. Discard it and reset to an operator-appropriate empty.
        if (this.valuesToCheckAreArrayOfObject(previousOperator)) {
          condition.values_to_check = MULTI_VALUE_OPERATORS.has(operator) ? [] : null
          return
        }
        if (MULTI_VALUE_OPERATORS.has(operator)) {
          condition.values_to_check = this.toMultiValueArray(condition.values_to_check)
          return
        }
        if (!Array.isArray(condition.values_to_check)) {
          return
        }
        // Typed-editor consumers keep the first value (a joined string is not a valid
        // select item); free-text consumers keep the historical comma-joined string.
        if (this.collapseMultiValueToFirst) {
          condition.values_to_check = condition.values_to_check.length ? condition.values_to_check[0] : null
          return
        }
        condition.values_to_check = condition.values_to_check.toString()
      },
    },
  }
</script>

<style scoped>
  .card-error {
    border-color: #ff5252 !important;
  }
</style>
